import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Ticket, 
  Mail, 
  Lock, 
  User, 
  ArrowRight, 
  Sparkles,
  Instagram,
  Youtube,
  Twitter,
  Users,
  AtSign,
  Check,
  X,
  Loader2,
  Zap,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function InfluencerSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isInfluencer, registerInfluencer, refreshUser, updateToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, formatted: '' });
  const [referralCode, setReferralCode] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [formData, setFormData] = useState({
    displayName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    bio: '',
    instagram: '',
    youtube: '',
    twitter: '',
    followerCount: ''
  });

  // Check for agency referral code in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      // Validate the referral code
      axios.get(`${API_URL}/agency/validate-referral/${ref}`)
        .then(res => {
          setAgencyName(res.data.agency_name);
        })
        .catch(() => {
          setReferralCode('');
        });
    }
  }, [searchParams]);

  // Redirect if already an influencer
  useEffect(() => {
    if (isInfluencer) {
      toast.info('You already have a creator account!');
      navigate('/dashboard');
    }
  }, [isInfluencer, navigate]);

  // Pre-fill display name if logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        displayName: user.display_name || '',
        email: user.email || ''
      }));
    }
  }, [isAuthenticated, user]);

  // Check username availability with debounce
  useEffect(() => {
    const checkUsername = async () => {
      const username = formData.username.trim();
      if (username.length < 3) {
        setUsernameStatus({ checking: false, available: null, formatted: '' });
        return;
      }

      setUsernameStatus(prev => ({ ...prev, checking: true }));
      
      try {
        const response = await axios.get(`${API_URL}/auth/check-username/${username}`);
        setUsernameStatus({
          checking: false,
          available: response.data.available,
          formatted: response.data.username
        });
      } catch (error) {
        setUsernameStatus({ checking: false, available: null, formatted: '' });
      }
    };

    const timer = setTimeout(checkUsername, 500);
    return () => clearTimeout(timer);
  }, [formData.username]);

  const handleUpgrade = async (e) => {
    e.preventDefault();
    
    if (!formData.username || formData.username.trim().length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    if (usernameStatus.available === false) {
      toast.error('Username is already taken');
      return;
    }

    setLoading(true);
    
    try {
      const socialHandles = {};
      if (formData.instagram) socialHandles.instagram = formData.instagram;
      if (formData.youtube) socialHandles.youtube = formData.youtube;
      if (formData.twitter) socialHandles.twitter = formData.twitter;

      const response = await axios.post(`${API_URL}/auth/upgrade-to-creator`, {
        username: formData.username,
        bio: formData.bio,
        social_handles: Object.keys(socialHandles).length > 0 ? socialHandles : null,
        follower_count: formData.followerCount ? parseInt(formData.followerCount) : null
      });

      // Update token and user state
      updateToken(response.data.token);
      
      // Refresh user data to update the UI
      await refreshUser();
      
      toast.success(`Welcome to FanBound Creators! Your profile is live at /${response.data.user.username}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upgrade account');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If logged in, use upgrade flow
    if (isAuthenticated) {
      return handleUpgrade(e);
    }
    
    if (step === 1) {
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (!formData.username || formData.username.trim().length < 3) {
        toast.error('Username must be at least 3 characters');
        return;
      }
      if (usernameStatus.available === false) {
        toast.error('Username is already taken');
        return;
      }
      setStep(2);
      return;
    }

    setLoading(true);
    
    try {
      const socialHandles = {};
      if (formData.instagram) socialHandles.instagram = formData.instagram;
      if (formData.youtube) socialHandles.youtube = formData.youtube;
      if (formData.twitter) socialHandles.twitter = formData.twitter;

      const user = await registerInfluencer({
        email: formData.email,
        password: formData.password,
        display_name: formData.displayName,
        username: formData.username,
        bio: formData.bio,
        social_handles: Object.keys(socialHandles).length > 0 ? socialHandles : null,
        follower_count: formData.followerCount ? parseInt(formData.followerCount) : null,
        referral_code: referralCode || null
      });
      
      toast.success(`Welcome to FanBound! Your profile is live at /${user.username}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Render upgrade form for logged-in users
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg space-y-8">
          {/* Logo & Header */}
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Ticket className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading font-bold text-2xl">FanBound</span>
            </Link>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-400 border border-green-500/30 mb-4">
              <Zap className="w-4 h-4" />
              <span className="font-medium">Quick Upgrade</span>
            </div>
            
            <h1 className="font-heading font-bold text-3xl">Upgrade to Creator</h1>
            <p className="text-muted-foreground mt-2">
              Hi {user?.display_name}! Complete your creator profile to start hosting events.
            </p>
          </div>

          {/* Upgrade Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {/* Username */}
              <div>
                <Label htmlFor="username">
                  Username *
                  <span className="text-muted-foreground text-xs ml-2">
                    This will be your unique URL: fanbound.com/@username
                  </span>
                </Label>
                <div className="relative mt-1">
                  <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                    className="pl-10 pr-10"
                    required
                    data-testid="upgrade-username"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameStatus.checking && <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />}
                    {!usernameStatus.checking && usernameStatus.available === true && <Check className="w-5 h-5 text-green-500" />}
                    {!usernameStatus.checking && usernameStatus.available === false && <X className="w-5 h-5 text-red-500" />}
                  </div>
                </div>
                {usernameStatus.formatted && (
                  <p className={`text-xs mt-1 ${usernameStatus.available ? 'text-green-500' : 'text-red-500'}`}>
                    {usernameStatus.available ? `${usernameStatus.formatted} is available!` : `${usernameStatus.formatted} is taken`}
                  </p>
                )}
              </div>

              {/* Bio */}
              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  placeholder="Tell your fans about yourself..."
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="mt-1 min-h-[100px]"
                  data-testid="upgrade-bio"
                />
              </div>

              {/* Social Handles */}
              <div className="space-y-3">
                <Label>Social Media Handles (optional)</Label>
                
                <div className="relative">
                  <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-500" />
                  <Input
                    type="text"
                    placeholder="Instagram username"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    className="pl-10"
                    data-testid="upgrade-instagram"
                  />
                </div>

                <div className="relative">
                  <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                  <Input
                    type="text"
                    placeholder="YouTube channel"
                    value={formData.youtube}
                    onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
                    className="pl-10"
                    data-testid="upgrade-youtube"
                  />
                </div>

                <div className="relative">
                  <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" />
                  <Input
                    type="text"
                    placeholder="X / Twitter handle"
                    value={formData.twitter}
                    onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                    className="pl-10"
                    data-testid="upgrade-twitter"
                  />
                </div>
              </div>

              {/* Follower Count */}
              <div>
                <Label htmlFor="followerCount">Total Followers (approximate)</Label>
                <div className="relative mt-1">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="followerCount"
                    type="number"
                    placeholder="e.g., 50000"
                    value={formData.followerCount}
                    onChange={(e) => setFormData({ ...formData, followerCount: e.target.value })}
                    className="pl-10"
                    data-testid="upgrade-followers"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Combined across all platforms
                </p>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full gap-2" 
              size="lg"
              disabled={loading || usernameStatus.available === false}
              data-testid="upgrade-submit"
            >
              {loading ? 'Upgrading...' : 'Become a Creator'}
              {!loading && <Sparkles className="w-4 h-4" />}
            </Button>
          </form>

          {/* Info */}
          <p className="text-center text-sm text-muted-foreground">
            You'll keep all your existing tickets and purchases as a creator.
          </p>
        </div>
      </div>
    );
  }

  // Render full signup form for non-logged-in users
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-lg space-y-8">
        {/* Logo & Header */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <Ticket className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl">FanBound</span>
          </Link>
          
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary border border-primary/30 mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="font-medium">Creator Account</span>
          </div>
          
          <h1 className="font-heading font-bold text-3xl">Become a Creator</h1>
          <p className="text-muted-foreground mt-2">
            Start monetizing your audience with exclusive events
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Already have a fan account? <Link to="/login" className="text-primary hover:underline">Log in first</Link> for quick upgrade!
          </p>
          
          {/* Agency Referral Banner */}
          {agencyName && (
            <div className="mt-4 p-3 bg-purple-500/20 border border-purple-500/30 rounded-lg flex items-center gap-2 justify-center">
              <Building2 className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 text-sm">Referred by <strong>{agencyName}</strong></span>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 1 ? 'bg-primary text-white' : 'bg-secondary'}`}>
              1
            </div>
            <span className="hidden sm:inline">Account</span>
          </div>
          <div className="w-12 h-0.5 bg-secondary" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 2 ? 'bg-primary text-white' : 'bg-secondary'}`}>
              2
            </div>
            <span className="hidden sm:inline">Profile</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {step === 1 ? (
            <div className="space-y-4">
              {/* Display Name */}
              <div>
                <Label htmlFor="displayName">Display Name / Stage Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Your creator name"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="creator-name"
                  />
                </div>
              </div>

              {/* Username */}
              <div>
                <Label htmlFor="username">
                  Username *
                  <span className="text-muted-foreground text-xs ml-2">
                    This will be your unique URL: fanbound.com/@username
                  </span>
                </Label>
                <div className="relative mt-1">
                  <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                    className="pl-10 pr-10"
                    required
                    data-testid="creator-username"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameStatus.checking && <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />}
                    {!usernameStatus.checking && usernameStatus.available === true && <Check className="w-5 h-5 text-green-500" />}
                    {!usernameStatus.checking && usernameStatus.available === false && <X className="w-5 h-5 text-red-500" />}
                  </div>
                </div>
                {usernameStatus.formatted && (
                  <p className={`text-xs mt-1 ${usernameStatus.available ? 'text-green-500' : 'text-red-500'}`}>
                    {usernameStatus.available ? `${usernameStatus.formatted} is available!` : `${usernameStatus.formatted} is taken`}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="creator-email"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="creator-password"
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="creator-confirm-password"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full gap-2" 
                size="lg"
                disabled={usernameStatus.available === false}
                data-testid="creator-next"
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Bio */}
              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  placeholder="Tell your fans about yourself..."
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="mt-1 min-h-[100px]"
                  data-testid="creator-bio"
                />
              </div>

              {/* Social Handles */}
              <div className="space-y-3">
                <Label>Social Media Handles (optional)</Label>
                
                <div className="relative">
                  <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-500" />
                  <Input
                    type="text"
                    placeholder="Instagram username"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    className="pl-10"
                    data-testid="creator-instagram"
                  />
                </div>

                <div className="relative">
                  <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                  <Input
                    type="text"
                    placeholder="YouTube channel"
                    value={formData.youtube}
                    onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
                    className="pl-10"
                    data-testid="creator-youtube"
                  />
                </div>

                <div className="relative">
                  <Twitter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" />
                  <Input
                    type="text"
                    placeholder="X / Twitter handle"
                    value={formData.twitter}
                    onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                    className="pl-10"
                    data-testid="creator-twitter"
                  />
                </div>
              </div>

              {/* Follower Count */}
              <div>
                <Label htmlFor="followerCount">Total Followers (approximate)</Label>
                <div className="relative mt-1">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="followerCount"
                    type="number"
                    placeholder="e.g., 50000"
                    value={formData.followerCount}
                    onChange={(e) => setFormData({ ...formData, followerCount: e.target.value })}
                    className="pl-10"
                    data-testid="creator-followers"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Combined across all platforms
                </p>
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 gap-2" 
                  size="lg"
                  disabled={loading}
                  data-testid="creator-submit"
                >
                  {loading ? 'Creating...' : 'Create Account'}
                  {!loading && <Sparkles className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}
        </form>

        {/* Links */}
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">
            Already have a creator account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
